import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, catchError, concatMap, debounceTime, filter } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Configuration for one sheet's edit pipeline. Every callback is the sheet's
 * EXISTING logic, transcribed verbatim — this factory is plumbing only and must
 * not normalize payloads, guards, or status mechanisms.
 */
export interface FormEditPipelineConfig {
  /** The form's valueChanges stream. */
  source$: Observable<unknown>;
  /** The sheet's existing debounce window. Prefer the named constants from `debounce-times.util.ts`. */
  debounceMs: number;
  /** Component DestroyRef for takeUntilDestroyed teardown. */
  destroyRef: DestroyRef;
  /** Optional guard; when it returns true the whole cycle is skipped (verbatim from the sheet). */
  shouldSkip?: () => boolean;
  /** Optional calculation step (calc sheets only). */
  calculate?: () => void;
  /** Persist step — the sheet's existing updateSheetData(...) call with its existing payload source. */
  persist: () => void;
  /** Optional validation; may be sync or async — it is awaited before status. */
  validate?: () => void | Promise<void>;
  /** Optional status emission — provide ONLY for sheets that emit status on a live edit today. */
  evaluateStatus?: () => void;
}

/**
 * Wire one ordered, debounced edit reaction:
 *   valueChanges -> debounce -> [shouldSkip?] -> calc?() -> persist() -> validate?() -> evaluateStatus?()
 *
 * concatMap (not switchMap) guarantees one emission — including an async validate —
 * fully completes before the next runs, so persist always precedes the status read
 * (fixes the calc-vs-persist race) and async validate finishes before status.
 *
 * Error isolation (DI-3): if any step throws or async validate rejects, the error is
 * caught and logged (dev only), then the pipeline resubscribes via catchError's `caught`
 * argument so subsequent emissions are still processed. Without this, a single thrown
 * error would silently kill the sheet's pipeline for the rest of the session.
 */
export function buildFormEditPipeline(cfg: FormEditPipelineConfig): void {
  cfg.source$
    .pipe(
      debounceTime(cfg.debounceMs),
      filter(() => !(cfg.shouldSkip?.() ?? false)),
      concatMap(async () => {
        cfg.calculate?.();
        cfg.persist();
        await cfg.validate?.();
        cfg.evaluateStatus?.();
      }),
      catchError((err, caught) => {
        // A step threw / async validate rejected. Log in dev and resubscribe so this
        // sheet's pipeline keeps processing subsequent edits instead of dying silently.
        if (!environment.production) {
          console.error('[FormEditPipeline] edit-cycle step threw; pipeline recovered:', err);
        }
        return caught;
      }),
      takeUntilDestroyed(cfg.destroyRef),
    )
    .subscribe();
}

/**
 * Configuration for an edit-mode sheet's LIVE (in-edit) feedback cycle. Unlike
 * {@link FormEditPipelineConfig} this has NO `persist` and NO cross-sheet
 * `validate` — by construction. Edit-mode sheets only emit user valueChanges
 * while a row is mid-edit, and during an edit nothing may commit or run
 * cross-sheet. Persist + cross-sheet validation move to the sheet's Tick handler.
 */
export interface EditModeLivePipelineConfig {
  source$: Observable<unknown>;
  debounceMs: number;
  destroyRef: DestroyRef;
  /** Verbatim per sheet (e.g. isLoadingState / isCalculating). */
  shouldSkip?: () => boolean;
  /** Same-sheet totals — live. */
  calculate?: () => void;
  /** SAME-SHEET validation only — live. Cross-sheet runs on Tick, not here. */
  validateSameSheet: () => void | Promise<void>;
  /**
   * Optional DISPLAY-ONLY live cross-sheet validation. Writes the component's
   * in-display validationErrors (inline red borders/messages) so cross-sheet
   * errors appear on the ACTIVE tab as the user edits. It MUST NOT persist
   * (no updateSheetData) and MUST NOT notify cross-tab — persist stays on Tick.
   * Awaited, so an async cross-sheet validate completes before evaluateStatus.
   * The dropdown verdict stays gated to save via the pure-verdict substitution
   * in FilingFormStateService.updateSheetLevelStatus (see that file).
   */
  validateCrossSheet?: () => void | Promise<void>;
  /** Same-sheet rolled-up verdict — live. */
  evaluateStatus?: () => void;
}

/**
 * Wire the live (in-edit) feedback cycle for an edit-mode sheet:
 *   valueChanges -> debounce -> [shouldSkip?] -> calculate?()
 *     -> await validateSameSheet() -> await validateCrossSheet?() -> evaluateStatus?()
 *
 * Identical plumbing to {@link buildFormEditPipeline} (concatMap ordering,
 * dev-logged catchError-resubscribe, takeUntilDestroyed). It CANNOT persist —
 * persist stays in the Tick handler. `validateCrossSheet` (optional) is
 * DISPLAY-ONLY: it surfaces cross-sheet red borders/messages live on the active
 * tab but does not commit. The sheet-selector verdict stays gated to save via
 * the pure-verdict substitution in FilingFormStateService.updateSheetLevelStatus.
 */
export function buildEditModeLivePipeline(cfg: EditModeLivePipelineConfig): void {
  cfg.source$
    .pipe(
      debounceTime(cfg.debounceMs),
      filter(() => !(cfg.shouldSkip?.() ?? false)),
      concatMap(async () => {
        cfg.calculate?.();
        await cfg.validateSameSheet();
        await cfg.validateCrossSheet?.();
        cfg.evaluateStatus?.();
      }),
      catchError((err, caught) => {
        if (!environment.production) {
          console.error('[EditModeLivePipeline] edit-cycle step threw; pipeline recovered:', err);
        }
        return caught;
      }),
      takeUntilDestroyed(cfg.destroyRef),
    )
    .subscribe();
}
